import type { ReactNode } from "react";
import type { LinkIcon } from "../shared/note-parse.ts";
import { SITE_MARKS } from "../shared/site-marks.ts";

/**
 * Brand marks.
 *
 * Official current logos, taken from simple-icons at 16.28.0 and inlined rather
 * than pulled from a package: there are a dozen of them, the whole file is
 * smaller than that dependency's index, and a logo that arrives over the network
 * is a logo that sometimes does not.
 *
 * Every social mark is drawn as a single monochrome path tinted with the brand's
 * own colour, which is what a compact rail wants. Instagram's real mark is a
 * gradient and X's is pure black; reproducing either faithfully at 15px would
 * make one muddy and the other invisible on an ink background, so the three
 * near-black marks are drawn in the page's foreground colour instead.
 *
 * Google Drive is the exception and keeps its six coloured segments, because it
 * is the only one here whose whole identity is the colour.
 */

type Props = { size?: number; className?: string };

/** viewBox 0 0 24 24, one path, tinted by `colour`. */
type Mark = { title: string; colour: string; path: string };

export const BRAND_MARKS: Record<string, Mark> = {
  instagram: {
    title: "Instagram",
    colour: "#E4405F",
    path: "M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-1.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8378.6165 19.074.321 18.2017.1197 16.9244.0645 15.6471.0093 15.236-.005 11.977.0014 8.718.0076 8.31.0215 7.0301.0839m.1402 21.6932c-1.17-.0509-1.8053-.2453-2.2287-.408-.5606-.216-.96-.4771-1.3819-.895-.422-.4178-.6811-.8186-.9-1.378-.1644-.4234-.3624-1.058-.4171-2.228-.0595-1.2645-.072-1.6442-.079-4.848-.007-3.2037.0053-3.583.0607-4.848.05-1.169.2456-1.805.408-2.2282.216-.5613.4762-.96.895-1.3816.4188-.4217.8184-.6814 1.3783-.9003.423-.1651 1.0575-.3614 2.227-.4171 1.2655-.06 1.6447-.072 4.848-.079 3.2033-.007 3.5835.005 4.8495.0608 1.169.0508 1.8053.2445 2.228.408.5608.216.96.4754 1.3816.895.4217.4194.6816.8176.9005 1.3787.1653.4217.3617 1.056.4169 2.2263.0602 1.2655.0739 1.645.0796 4.848.0058 3.203-.0055 3.5834-.061 4.848-.051 1.17-.245 1.8055-.408 2.2294-.216.5604-.4763.96-.8954 1.3814-.419.4215-.8181.6811-1.3783.9-.4224.1649-1.0577.3617-2.2262.4174-1.2656.0595-1.6448.072-4.8493.079-3.2045.007-3.5825-.006-4.848-.0608M16.953 5.5864A1.44 1.44 0 1 0 18.39 4.144a1.44 1.44 0 0 0-1.437 1.4424M5.8385 12.012c.0067 3.4032 2.7706 6.1557 6.173 6.1493 3.4026-.0065 6.157-2.7701 6.1506-6.1733-.0065-3.4032-2.771-6.1565-6.174-6.1498-3.403.0067-6.156 2.771-6.1496 6.1738M8 12.0077a4 4 0 1 1 4.008 3.9921A3.9996 3.9996 0 0 1 8 12.0077",
  },
  onlyfans: {
    title: "OnlyFans",
    colour: "#00AFF0",
    path: "M24 4.003h-4.015c-3.45 0-5.3.197-6.748 1.957a7.996 7.996 0 1 0 2.103 9.211c3.182-.231 5.39-2.134 6.085-5.173 0 0-2.399.585-4.43 0 4.018-.777 6.333-3.037 7.005-5.995zM5.61 11.999A2.391 2.391 0 0 1 9.28 9.97a2.966 2.966 0 0 1 2.998-2.528h.008c-.92 1.778-1.407 3.352-1.998 5.263A2.392 2.392 0 0 1 5.61 12Zm2.386-7.996a7.996 7.996 0 1 0 7.996 7.996 7.996 7.996 0 0 0-7.996-7.996Zm0 10.394A2.399 2.399 0 1 1 10.395 12a2.396 2.396 0 0 1-2.399 2.398Z",
  },
  fansly: {
    title: "Fansly",
    // Not in simple-icons, which leaves adult brands out. Traced from the
    // mask icon fansly.com itself serves (safari-pinned-tab.svg), refit to
    // 24x24, in the colour it declares for it.
    colour: "#279EFF",
    path: "M6.4 3.693c-1.693 0.227 -3.24 1.24 -3.947 2.613c-0.68 1.307 -0.72 3.04 -0.08 4.373c0.36 0.747 0.933 1.373 4.813 5.253c3.853 3.84 4.453 4.387 4.813 4.4c0.373 0.013 0.787 -0.347 3.693 -3.253l3.267 -3.253l-2.627 -2.627l-2.613 -2.613l1.133 -1.08c0.787 -0.76 1.28 -1.133 1.64 -1.227c1.64 -0.44 3.08 0.613 3.12 2.293c0.027 0.88 -0.187 1.293 -1.133 2.227c-0.413 0.4 -0.747 0.773 -0.747 0.827c0 0.107 0.907 1.053 1.413 1.48c0.28 0.24 0.307 0.227 1.173 -0.667c0.56 -0.56 1.053 -1.24 1.32 -1.773c0.387 -0.773 0.427 -0.973 0.427 -2.067c0 -1.013 -0.053 -1.32 -0.36 -1.947c-1.24 -2.667 -4.2 -3.68 -6.84 -2.36c-0.307 0.16 -2.093 1.813 -4.293 4c-2.253 2.253 -3.813 3.707 -3.893 3.64c-0.093 -0.053 -0.56 -0.507 -1.053 -1.013c-1.067 -1.107 -1.387 -1.813 -1.213 -2.747c0.267 -1.44 1.627 -2.293 3.04 -1.907c0.573 0.147 0.547 0.133 1.64 1.187l0.747 0.72l0.813 -0.813c0.453 -0.44 0.813 -0.853 0.813 -0.92c0 -0.227 -1.787 -1.853 -2.333 -2.133c-0.84 -0.44 -2.04 -0.707 -2.733 -0.613Zm6.973 8.133c1.627 1.147 1.36 3.467 -0.467 4.24c-1 0.413 -2.173 0.093 -2.853 -0.8c-0.507 -0.68 -0.6 -1.787 -0.2 -2.56c0.667 -1.307 2.333 -1.733 3.52 -0.88ZM11.32 12.413c-0.453 0.173 -0.907 1 -0.84 1.56c0.187 1.547 2.24 1.907 2.907 0.507c0.36 -0.773 0.24 -1.28 -0.307 -1.28c-0.44 -0 -0.68 -0.227 -0.68 -0.627c0 -0.253 -0.067 -0.307 -0.36 -0.293c-0.213 -0 -0.533 0.067 -0.72 0.133Z",
  },
  reddit: {
    title: "Reddit",
    colour: "#FF4500",
    path: "M12 0C5.373 0 0 5.373 0 12c0 3.314 1.343 6.314 3.515 8.485l-2.286 2.286C.775 23.225 1.097 24 1.738 24H12c6.627 0 12-5.373 12-12S18.627 0 12 0Zm4.388 3.199c1.104 0 1.999.895 1.999 1.999 0 1.105-.895 2-1.999 2-.946 0-1.739-.657-1.947-1.539v.002c-1.147.162-2.032 1.15-2.032 2.341v.007c1.776.067 3.4.567 4.686 1.363.473-.363 1.064-.58 1.707-.58 1.547 0 2.802 1.254 2.802 2.802 0 1.117-.655 2.081-1.601 2.531-.088 3.256-3.637 5.876-7.997 5.876-4.361 0-7.905-2.617-7.998-5.87-.954-.447-1.614-1.415-1.614-2.538 0-1.548 1.255-2.802 2.803-2.802.645 0 1.239.218 1.712.585 1.275-.79 2.881-1.291 4.64-1.365v-.01c0-1.663 1.263-3.034 2.88-3.207.188-.911.993-1.595 1.959-1.595Zm-8.085 8.376c-.784 0-1.459.78-1.506 1.797-.047 1.016.64 1.429 1.426 1.429.786 0 1.371-.369 1.418-1.385.047-1.017-.553-1.841-1.338-1.841Zm7.406 0c-.786 0-1.385.824-1.338 1.841.047 1.017.634 1.385 1.418 1.385.785 0 1.473-.413 1.426-1.429-.046-1.017-.721-1.797-1.506-1.797Zm-3.703 4.013c-.974 0-1.907.048-2.77.135-.147.015-.241.168-.183.305.483 1.154 1.622 1.964 2.953 1.964 1.33 0 2.47-.81 2.953-1.964.057-.137-.037-.29-.184-.305-.863-.087-1.795-.135-2.769-.135Z",
  },
  twitter: {
    title: "X",
    colour: "#F2EFE8",
    path: "M14.234 10.162 22.977 0h-2.072l-7.591 8.824L7.251 0H.258l9.168 13.343L.258 24H2.33l8.016-9.318L16.749 24h6.993zm-2.837 3.299-.929-1.329L3.076 1.56h3.182l5.965 8.532.929 1.329 7.754 11.09h-3.182z",
  },
  tiktok: {
    title: "TikTok",
    colour: "#F2EFE8",
    path: "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z",
  },
  facebook: {
    title: "Facebook",
    colour: "#0866FF",
    path: "M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z",
  },
  threads: {
    title: "Threads",
    colour: "#F2EFE8",
    path: "M18.263 11.097c-.03-3.486-1.92-5.586-5.111-5.586-2.13 0-3.922.963-4.863 2.499l2.062 1.438c.535-.843 1.272-1.543 2.628-1.543 1.528 0 2.318.85 2.544 2.431a15 15 0 0 0-2.236-.173c-4.125 0-6.068 1.867-6.068 4.336s1.943 3.99 4.804 3.99c3.139 0 5.013-2.115 5.781-4.735.798.361 1.348 1.204 1.348 2.47 0 3.387-3.907 5.232-7.22 5.232-4.885 0-8.077-3.207-8.077-8.424 0-6.392 4.223-10.487 9.9-10.487 3.808 0 5.69 1.671 6.97 3.914l2.108-1.475C21.44 2.078 18.331 0 13.663 0 6.227 0 1.168 5.277 1.168 12.934c0 7 4.953 11.066 10.856 11.066 4.878 0 9.809-2.846 9.809-7.716 0-2.545-1.46-4.231-3.569-5.187m-6.33 4.855c-1.077 0-2.026-.512-2.026-1.453 0-1.483 1.822-1.934 3.606-1.934.678 0 1.34.045 1.927.173-.422 1.927-1.671 3.215-3.508 3.214Z",
  },
  youtube: {
    title: "YouTube",
    colour: "#FF0000",
    path: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
  },
  snapchat: {
    title: "Snapchat",
    colour: "#FFFC00",
    path: "M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.074.42.194.789.3 1.104.3.234 0 .384-.06.465-.105l-.046-.569c-.098-1.626-.225-3.651.307-4.837C7.392 1.077 10.739.807 11.727.807l.419-.015h.06z",
  },
  telegram: {
    title: "Telegram",
    colour: "#26A5E4",
    path: "M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z",
  },
  linktree: {
    title: "Linktree",
    colour: "#43E55E",
    path: "m13.73635 5.85251 4.00467-4.11665 2.3248 2.3808-4.20064 4.00466h5.9085v3.30473h-5.9365l4.22865 4.10766-2.3248 2.3338L12.0005 12.099l-5.74052 5.76852-2.3248-2.3248 4.22864-4.10766h-5.9375V8.12132h5.9085L3.93417 4.11666l2.3248-2.3808 4.00468 4.11665V0h3.4727zm-3.4727 10.30614h3.4727V24h-3.4727z",
  },
};

/** The six coloured segments of the current Google Drive mark, viewBox 0 0 256 229. */
const DRIVE: Array<{ fill: string; d: string }> = [
  { fill: "#0066da", d: "m19.354 196.034l11.29 19.5c2.346 4.106 5.718 7.332 9.677 9.678q17.009-21.591 23.68-33.137q6.77-11.717 16.641-36.655q-26.604-3.502-40.32-3.502q-13.165 0-40.322 3.502c0 4.545 1.173 9.09 3.519 13.196z" },
  { fill: "#ea4335", d: "M215.681 225.212c3.96-2.346 7.332-5.572 9.677-9.677l4.692-8.064l22.434-38.855a26.57 26.57 0 0 0 3.518-13.196q-27.315-3.502-40.247-3.502q-13.899 0-40.248 3.502q9.754 25.075 16.422 36.655q6.724 11.683 23.752 33.137" },
  { fill: "#00832d", d: "M128.001 73.311q19.68-23.768 27.125-36.655q5.996-10.377 13.196-33.137C164.363 1.173 159.818 0 155.126 0h-54.25C96.184 0 91.64 1.32 87.68 3.519q9.16 26.103 15.544 37.154q7.056 12.213 24.777 32.638" },
  { fill: "#2684fc", d: "M175.36 155.42H80.642l-40.32 69.792c3.958 2.346 8.503 3.519 13.195 3.519h148.968c4.692 0 9.238-1.32 13.196-3.52z" },
  { fill: "#00ac47", d: "M128.001 73.311L87.681 3.52c-3.96 2.346-7.332 5.571-9.678 9.677L3.519 142.224A26.57 26.57 0 0 0 0 155.42h80.642z" },
  { fill: "#ffba00", d: "m215.242 77.71l-37.243-64.514c-2.345-4.106-5.718-7.331-9.677-9.677l-40.32 69.792l47.358 82.109h80.496c0-4.546-1.173-9.09-3.519-13.196z" },
];

export function DriveMark({ size = 15, className }: Props) {
  return (
    <svg width={size} height={(size * 229) / 256} viewBox="0 0 256 229" className={className} aria-hidden>
      {DRIVE.map((segment) => (
        <path key={segment.fill + segment.d.slice(0, 8)} fill={segment.fill} d={segment.d} />
      ))}
    </svg>
  );
}

/**
 * One social mark, or null when the platform has none.
 *
 * Returning null rather than a placeholder is deliberate: the caller falls back
 * to a lettered chip, which reads better than a generic globe pretending to be
 * a brand.
 */
export function BrandMark({ platform, size = 15, className }: Props & { platform: string }) {
  const mark = BRAND_MARKS[platform];
  if (!mark) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden focusable="false">
      <path fill={mark.colour} d={mark.path} />
    </svg>
  );
}

export function brandColour(platform: string): string | null {
  return BRAND_MARKS[platform]?.colour ?? null;
}

export function hasBrandMark(platform: string): boolean {
  return platform in BRAND_MARKS;
}

/**
 * Platforms with no icon anybody publishes.
 *
 * simple-icons excludes adult brands as a matter of policy, and drawing a
 * trademark from memory produces something that is recognisably wrong rather
 * than recognisably theirs. A monogram in the brand's real colour is honest
 * about being a stand-in and still tells them apart at a glance, which is the
 * job. Swap any of these for a real SVG the moment one is to hand.
 */
export const MONOGRAMS: Record<string, { colour: string; letters: string }> = {
  pornhub: { colour: "#FF9000", letters: "PH" },
  chaturbate: { colour: "#F47321", letters: "CB" },
  xvideos: { colour: "#C9211E", letters: "XV" },
  xhamster: { colour: "#E36C0A", letters: "XH" },
  stripchat: { colour: "#E51F5E", letters: "SC" },
  manyvids: { colour: "#43B0E4", letters: "MV" },
  redgifs: { colour: "#F44336", letters: "RG" },
  bongacams: { colour: "#D8232A", letters: "BC" },
  cam4: { colour: "#F7941E", letters: "C4" },
  livejasmin: { colour: "#C8102E", letters: "LJ" },
  myfreecams: { colour: "#0F70B7", letters: "MF" },
  clips4sale: { colour: "#E4002B", letters: "C4S" },
  fetlife: { colour: "#B3282D", letters: "FL" },
  throne: { colour: "#7C3AED", letters: "TH" },
  beacons: { colour: "#EF4444", letters: "BE" },
};

/**
 * A site's own mark, falling back to a monogram.
 *
 * The favicon first, because at fourteen pixels in the middle of a sentence a
 * two-letter monogram is a grey square and the point of a badge is being
 * recognised without being read. The monogram stays for the handful of sites
 * whose favicon is nothing but a generic globe.
 */
export function Monogram({ platform, size = 14 }: Props & { platform: string }) {
  const src = SITE_MARKS[platform];
  if (src) {
    return (
      <img
        className="site-mark"
        src={src}
        alt=""
        width={size}
        height={size}
        style={{ width: size, height: size }}
        aria-hidden
      />
    );
  }

  const mark = MONOGRAMS[platform];
  if (!mark) return null;
  return (
    <span
      className="rail-letter brand"
      style={{ width: size, height: size, color: mark.colour, borderColor: `${mark.colour}55` }}
      aria-hidden
    >
      {mark.letters}
    </span>
  );
}

export function hasMonogram(platform: string): boolean {
  if (SITE_MARKS[platform]) return true;
  return platform in MONOGRAMS;
}

/** The last resort, for a link to somewhere nobody has a mark for. */
export function GlobeIcon({ size = 14, className }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.6 3.8 5.7 3.8 9S14.5 18.4 12 21c-2.5-2.6-3.8-5.7-3.8-9S9.5 5.6 12 3Z" />
    </svg>
  );
}

/** Google's own four-colour G, for the sign-in button. */
export function GoogleMark({ size = 15, className }: Props) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" className={className} aria-hidden focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5Z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65Z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19Z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48Z" />
    </svg>
  );
}

// ── Google Workspace ─────────────────────────────────────────────────────

/**
 * Drive, Sheets, Docs and Slides.
 *
 * Drive keeps its published six-segment mark. The other three are drawn here
 * rather than inlined from an icon set: the current published versions are built
 * from masks, filters and gradients, which need unique element ids per instance
 * to avoid colliding on a page that renders several. The classic flat marks are
 * a page, a folded corner and some white content, which is both trivial to draw
 * correctly and legible at fourteen pixels, where the fancy ones are mud.
 */
export type WorkspaceKind = "drive" | "sheets" | "docs" | "slides" | "forms";

const PAGE = "M14.5 2H6.5A2.5 2.5 0 0 0 4 4.5v15A2.5 2.5 0 0 0 6.5 22h11a2.5 2.5 0 0 0 2.5-2.5V7.5L14.5 2Z";
const FOLD = "M14.5 2v4A1.5 1.5 0 0 0 16 7.5h4L14.5 2Z";

const DOC_KINDS: Record<Exclude<WorkspaceKind, "drive">, { body: string; fold: string; content: ReactNode }> = {
  sheets: {
    body: "#0F9D58",
    fold: "#87CEAC",
    content: (
      <>
        <rect x="7.5" y="11" width="9" height="7" rx="0.6" fill="#fff" />
        <path d="M7.5 13.4h9M7.5 15.7h9M12 11v7" stroke="#0F9D58" strokeWidth="0.9" />
      </>
    ),
  },
  docs: {
    body: "#4285F4",
    fold: "#A1C2FA",
    content: (
      <>
        <rect x="7.5" y="11.5" width="9" height="1.3" rx="0.65" fill="#fff" />
        <rect x="7.5" y="14.3" width="9" height="1.3" rx="0.65" fill="#fff" />
        <rect x="7.5" y="17.1" width="6" height="1.3" rx="0.65" fill="#fff" />
      </>
    ),
  },
  slides: {
    body: "#F4B400",
    fold: "#FDE293",
    content: <rect x="7.5" y="11.8" width="9" height="6.2" rx="0.8" fill="#fff" />,
  },
  forms: {
    body: "#7248B9",
    fold: "#C5AEE8",
    content: (
      <>
        <rect x="7.5" y="11.5" width="1.6" height="1.6" rx="0.3" fill="#fff" />
        <rect x="10.4" y="11.7" width="6" height="1.2" rx="0.6" fill="#fff" />
        <rect x="7.5" y="14.6" width="1.6" height="1.6" rx="0.3" fill="#fff" />
        <rect x="10.4" y="14.8" width="6" height="1.2" rx="0.6" fill="#fff" />
        <rect x="7.5" y="17.7" width="1.6" height="1.6" rx="0.3" fill="#fff" />
        <rect x="10.4" y="17.9" width="4" height="1.2" rx="0.6" fill="#fff" />
      </>
    ),
  },
};

export function WorkspaceMark({ kind, size = 14, className }: Props & { kind: WorkspaceKind }) {
  if (kind === "drive") return <DriveMark size={size} className={className} />;

  const spec = DOC_KINDS[kind];
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden focusable="false">
      <path d={PAGE} fill={spec.body} />
      <path d={FOLD} fill={spec.fold} />
      {spec.content}
    </svg>
  );
}

/**
 * The right mark for a platform, decided once.
 *
 * A published logo where one exists. A monogram in the brand's real colour for
 * the tube and cam sites, which no icon set carries and which are worse served
 * by a logo drawn from memory than by an honest stand-in. A globe for somewhere
 * nobody has a mark for at all.
 *
 * The rails and the inline buttons in a note both need this answer, and two
 * copies of it is how the same account ends up wearing two different badges
 * depending on where you look at it.
 */
export function PlatformMark({ platform, size = 14 }: Props & { platform: string }) {
  if (hasBrandMark(platform)) return <BrandMark platform={platform} size={size} />;
  if (hasMonogram(platform)) return <Monogram platform={platform} size={size} />;
  return <GlobeIcon size={size} className="rail-globe" />;
}

/** The mark a parsed note link asks for, whatever kind it turned out to be. */
export function LinkMark({ icon, size = 13 }: Props & { icon: LinkIcon }) {
  if (icon.kind === "workspace") return <WorkspaceMark kind={icon.workspace} size={size} />;
  if (icon.kind === "platform") return <PlatformMark platform={icon.platform} size={size} />;
  return <GlobeIcon size={size} className="rail-globe" />;
}
